import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import DiscountSection from "../../components/DiscountSection";
// import CollapsibleCard from "../../components/CollapsibleCard";
import SectionDivider from "../../components/SectionDivider";
import {
  updateStudent,
  deleteStudent,
  downloadStudentInfo,
  getStudentTransactions,
} from "../../services/StudentServices";
import {
  getAllEducationLevels,
  getEducationLevelSubclass,
} from "../../services/educationLevelServices";
import { getAllDiscounts } from "../../services/schoolDiscountsServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import downloadIcon from "../../assets/download-icon.svg";
import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import HotdropdownV2 from "../../components/HotdropdownV2";
import CollapsibleCard from "../attendance-system/CollapsibleCard";

const EditStudentModal = ({
  isOpen,
  onClose,
  student,
  onStudentUpdated,
  setPagination,
}) => {
  const { t } = useTranslation("general");

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAdditional, setIsEditingAdditional] = useState(false);
  const [educationLevels, setEducationLevels] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [discountType, setDiscountType] = useState("percentage");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState(null);
  const [subclasses, setSubclasses] = useState([]);

  const [studentFields, setStudentFields] = useState({
    fullName: "",
    academicLevel: {
      title: "",
      value: "",
      price: 0,
    },
    subclass: {
      id: "",
      name: "",
    },
    source: "",
    discount: {
      title: "",
      value: "",
    },
    discountAmount: 0,
    phoneNumber: "",
    secondPhoneNumber: "",
    notes: "",
  });

  // Additional info fields state
  const [additionalFields, setAdditionalFields] = useState({
    // Student Info
    dayOfBirth: "",
    monthOfBirth: "",
    yearOfBirth: "",
    unifiedCardNumber: "",
    civilStatusIdNumber: "",
    mahala: "",
    zoqaq: "",
    dar: "",
    nearestPoint: "",
    numberOfFamilyNumber: "",
    studentOrderAmongFamily: "",
    livingWith: "",
    // Father Info
    fatherName: "",
    fatherAlive: null,
    fatherUnifiedCardNumber: "",
    fatherCivilStatusIdNumber: "",
    fatherNationalityCertificateNumber: "",
    fatherDayOfBirth: "",
    fatherMonthOfBirth: "",
    fatherYearOfBirth: "",
    fatherPhoneNumber: "",
    fatherAcademicAchievement: "",
    fatherCurrentJob: "",
    // Mother Info
    motherName: "",
    motherAlive: null,
    motherDayOfBirth: "",
    motherMonthOfBirth: "",
    motherYearOfBirth: "",
    motherPhoneNumber: "",
    motherAcademicAchievement: "",
    motherCurrentJob: "",
  });

  const getStudentAdditionalInfo = async () => {
    try {
      const response = await getStudentTransactions(student.id);

      if (
        response.data &&
        response.data.studentAdditionalInfo &&
        response.data.studentAdditionalInfo.length > 0
      ) {
        const additionalInfo = response.data.studentAdditionalInfo[0];
        setAdditionalInfo(additionalInfo);

        // Set additional fields state
        setAdditionalFields({
          dayOfBirth: additionalInfo.dayOfBirth || "",
          monthOfBirth: additionalInfo.monthOfBirth || "",
          yearOfBirth: additionalInfo.yearOfBirth || "",
          unifiedCardNumber: additionalInfo.unifiedCardNumber || "",
          civilStatusIdNumber: additionalInfo.civilStatusIdNumber || "",
          mahala: additionalInfo.mahala || "",
          zoqaq: additionalInfo.zoqaq || "",
          dar: additionalInfo.dar || "",
          nearestPoint: additionalInfo.nearestPoint || "",
          numberOfFamilyNumber: additionalInfo.numberOfFamilyNumber || "",
          studentOrderAmongFamily: additionalInfo.studentOrderAmongFamily || "",
          livingWith: additionalInfo.livingWith || "",
          fatherName: additionalInfo.fatherName || "",
          fatherAlive: additionalInfo.fatherAlive,
          fatherUnifiedCardNumber: additionalInfo.fatherUnifiedCardNumber || "",
          fatherCivilStatusIdNumber:
            additionalInfo.fatherCivilStatusIdNumber || "",
          fatherNationalityCertificateNumber:
            additionalInfo.fatherNationalityCertificateNumber || "",
          fatherDayOfBirth: additionalInfo.fatherDayOfBirth || "",
          fatherMonthOfBirth: additionalInfo.fatherMonthOfBirth || "",
          fatherYearOfBirth: additionalInfo.fatherYearOfBirth || "",
          fatherPhoneNumber: additionalInfo.fatherPhoneNumber || "",
          fatherAcademicAchievement:
            additionalInfo.fatherAcademicAchievement || "",
          fatherCurrentJob: additionalInfo.fatherCurrentJob || "",
          motherName: additionalInfo.motherName || "",
          motherAlive: additionalInfo.motherAlive,
          motherDayOfBirth: additionalInfo.motherDayOfBirth || "",
          motherMonthOfBirth: additionalInfo.motherMonthOfBirth || "",
          motherYearOfBirth: additionalInfo.motherYearOfBirth || "",
          motherPhoneNumber: additionalInfo.motherPhoneNumber || "",
          motherAcademicAchievement:
            additionalInfo.motherAcademicAchievement || "",
          motherCurrentJob: additionalInfo.motherCurrentJob || "",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Initialize student data when modal opens or student changes
  useEffect(() => {
    if (student && isOpen) {
      // Find the active student year to get current class and other info
      const activeStudentYear = student.activeYear;

      setStudentFields({
        fullName: student.fullName || "",
        academicLevel: {
          title: activeStudentYear?.schoolClass?.name || "",
          value: activeStudentYear?.schoolClass?.id || "",
          price: activeStudentYear?.schoolClass?.price || 0,
        },
        subclass: {
          id: activeStudentYear?.schoolSubClass?.id || "",
          name: activeStudentYear?.schoolSubClass?.name || "",
        },
        source: activeStudentYear?.source || "",
        discount: {
          title:
            activeStudentYear?.discount?.name ||
            (activeStudentYear?.discountAmount ? "other" : ""),
          value:
            activeStudentYear?.discountId ||
            (activeStudentYear?.discountAmount ? "" : ""),
        },
        discountAmount: activeStudentYear?.discountAmount || 0,
        phoneNumber: student.phoneNumber1 || "",
        secondPhoneNumber: student.phoneNumber2 || "",
        notes: student.notes || "",
      });
      // Add this line after setting studentFields in the useEffect:
      if (activeStudentYear?.discountAmount && !activeStudentYear?.discount) {
        setDiscountType(
          activeStudentYear.isPercentage ? "percentage" : "fixed"
        );
      }
      if (activeStudentYear?.schoolClass?.id) {
        fetchSubclasses(activeStudentYear.schoolClass.id);
      }
      setIsEditing(false);
      setIsEditingAdditional(false);
      setError(false);
      setErrorMessage("");
      getStudentAdditionalInfo();
    }
  }, [student, isOpen]);

  const fetchEducationLevels = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm, // ✅ Add this line
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      // ✅ Handle pagination properly
      if (page === 1) {
        setEducationLevels(transformedData);
      } else {
        setEducationLevels((prev) => [...prev, ...transformedData]);
      }

      // ✅ Return the expected structure
      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      setEducationLevels([]);

      // ✅ Return error structure
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const fetchSubclasses = async (educationLevelId) => {
    try {
      const response = await getEducationLevelSubclass(educationLevelId);
      const formattedSubclasses = response.data.subClasses.map((subclass) => ({
        title: `${subclass.name} (${t("students")}: ${subclass.studentCount})`,
        value: subclass.id,
        name: subclass.name,
      }));
      setSubclasses(formattedSubclasses);
    } catch (error) {
      console.error("Error fetching subclasses:", error);
      setSubclasses([]);
    }
  };

  const fetchAllDiscounts = async () => {
    try {
      const response = await getAllDiscounts();

      if (response.status === 200) {
        const formatedDiscounts = response.data.discounts.map((discount) => ({
          title: discount.name,
          value: discount.id,
        }));

        setDiscounts([
          ...formatedDiscounts,
          { title: "other", value: "" },
          { title: "none", value: "" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching discounts:", error);
    }
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setStudentFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleAdditionalInputChange = (e, fieldName) => {
    const value = e.target.value;
    setAdditionalFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const validateName = (name) => {
    if (!name?.trim()) {
      return t("inputFieldCannotBeEmpty");
    }
    if (name.length < 3) {
      return t("nameTooShort");
    }
    return "";
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();

    // Validation
    const nameError = validateName(studentFields.fullName);
    if (nameError) {
      setError(true);
      setErrorMessage(nameError);
      return;
    }

    if (!studentFields.academicLevel.value || !studentFields.phoneNumber) {
      setError(true);
      setErrorMessage(t("emptyInputFields"));
      return;
    }

    try {
      const updateData = {
        fullName: studentFields.fullName,
        source: studentFields.source,
        notes: studentFields.notes,
        schoolClassId: studentFields.academicLevel.value,
        schoolSubClassId: studentFields.subclass.id,
        phoneNumber1: studentFields.phoneNumber,
        phoneNumber2: studentFields.secondPhoneNumber,
        ...additionalFields,
      };

      if (!studentFields.discount.value) {
        updateData.discountedAmount = parseFloat(studentFields.discountAmount);
        updateData.isPercentage = discountType === "percentage";
      } else {
        updateData.schoolDiscountId = studentFields.discount.value;
      }

      const numericFields = [
        "dayOfBirth",
        "monthOfBirth",
        "yearOfBirth",
        "numberOfFamilyNumber",
        "studentOrderAmongFamily",
        "fatherDayOfBirth",
        "fatherMonthOfBirth",
        "fatherYearOfBirth",
        "motherDayOfBirth",
        "motherMonthOfBirth",
        "motherYearOfBirth",
      ];

      numericFields.forEach((field) => {
        if (updateData[field] !== undefined && updateData[field] !== null) {
          if (updateData[field] !== undefined && updateData[field] !== null) {
            updateData[field] =
              updateData[field] == "" ? null : parseInt(updateData[field]);
          }
        }
      });

      setLoadingModal(true);
      const response = await updateStudent(student.id, updateData);

      if (response.status === 200) {
        setError(false);
        setErrorMessage("");
        setIsEditing(false);

        // Call parent callback to refresh data
        if (onStudentUpdated) {
          onStudentUpdated();
        }

        // Don't close modal immediately, just show success state
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        setError(true);
        setErrorMessage(errorMessage);
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleDeleteStudent = async () => {
    try {
      setLoadingModal(true);
      const response = await deleteStudent(student.id);

      if (response.status === 200) {
        if (setPagination) {
          setPagination((prev) => ({
            ...prev,
            totalRecords: prev.totalRecords - 1,
          }));
        }

        if (onStudentUpdated) {
          onStudentUpdated();
        }

        handleClose();
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      setError(true);
      setErrorMessage("Failed to delete student");
    } finally {
      setShowDeleteConfirmation(false);
      setLoadingModal(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    setIsEditingAdditional(false);
    setError(false);
    setErrorMessage("");
    onClose();
  };

  const handleCancel = () => {
    if (isEditing || isEditingAdditional) {
      // Reset to original student data
      if (student) {
        const activeStudentYear = student.activeYear;

        setStudentFields({
          fullName: student.fullName || "",
          academicLevel: {
            title: activeStudentYear?.schoolClass?.name || "",
            value: activeStudentYear?.schoolClass?.id || "",
            price: activeStudentYear?.schoolClass?.price || 0,
          },
          subclass: {
            id: activeStudentYear?.schoolSubClass?.id || "",
            name: activeStudentYear?.schoolSubClass?.name || "",
          },
          source: activeStudentYear?.source || "",
          discount: {
            title:
              activeStudentYear?.discount?.name ||
              (activeStudentYear?.discountAmount ? "other" : ""),
            value:
              activeStudentYear?.discountId ||
              (activeStudentYear?.discountAmount ? "" : ""),
          },
          discountAmount: activeStudentYear?.discountAmount || 0,
          phoneNumber: student.phoneNumber1 || "",
          secondPhoneNumber: student.phoneNumber2 || "",
          notes: student.notes || "",
        });

        setSubclasses([]);

        // Reset additional fields if they exist
        if (additionalInfo) {
          setAdditionalFields({
            dayOfBirth: additionalInfo.dayOfBirth || "",
            monthOfBirth: additionalInfo.monthOfBirth || "",
            yearOfBirth: additionalInfo.yearOfBirth || "",
            unifiedCardNumber: additionalInfo.unifiedCardNumber || "",
            civilStatusIdNumber: additionalInfo.civilStatusIdNumber || "",
            mahala: additionalInfo.mahala || "",
            zoqaq: additionalInfo.zoqaq || "",
            dar: additionalInfo.dar || "",
            nearestPoint: additionalInfo.nearestPoint || "",
            numberOfFamilyNumber: additionalInfo.numberOfFamilyNumber || "",
            studentOrderAmongFamily:
              additionalInfo.studentOrderAmongFamily || "",
            livingWith: additionalInfo.livingWith || "",
            fatherName: additionalInfo.fatherName || "",
            fatherAlive: additionalInfo.fatherAlive,
            fatherUnifiedCardNumber:
              additionalInfo.fatherUnifiedCardNumber || "",
            fatherCivilStatusIdNumber:
              additionalInfo.fatherCivilStatusIdNumber || "",
            fatherNationalityCertificateNumber:
              additionalInfo.fatherNationalityCertificateNumber || "",
            fatherDayOfBirth: additionalInfo.fatherDayOfBirth || "",
            fatherMonthOfBirth: additionalInfo.fatherMonthOfBirth || "",
            fatherYearOfBirth: additionalInfo.fatherYearOfBirth || "",
            fatherPhoneNumber: additionalInfo.fatherPhoneNumber || "",
            fatherAcademicAchievement:
              additionalInfo.fatherAcademicAchievement || "",
            fatherCurrentJob: additionalInfo.fatherCurrentJob || "",
            motherName: additionalInfo.motherName || "",
            motherAlive: additionalInfo.motherAlive,
            motherDayOfBirth: additionalInfo.motherDayOfBirth || "",
            motherMonthOfBirth: additionalInfo.motherMonthOfBirth || "",
            motherYearOfBirth: additionalInfo.motherYearOfBirth || "",
            motherPhoneNumber: additionalInfo.motherPhoneNumber || "",
            motherAcademicAchievement:
              additionalInfo.motherAcademicAchievement || "",
            motherCurrentJob: additionalInfo.motherCurrentJob || "",
          });
        }
      }
      if (activeStudentYear?.discountAmount && !activeStudentYear?.discount) {
        setDiscountType(
          activeStudentYear.isPercentage ? "percentage" : "fixed"
        );
      }
      setIsEditing(false);
      setIsEditingAdditional(false);
      setError(false);
      setErrorMessage("");
    } else {
      handleClose();
    }
  };

  const handleDownloadStudentInfo = async () => {
    try {
      const response = await downloadStudentInfo(student.id);

      // Get content type from response headers
      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "application/octet-stream";

      // Determine file extension based on content type
      let extension = ".pdf"; // default
      if (contentType.includes("wordprocessingml.document")) {
        extension = ".docx";
      } else if (contentType.includes("application/pdf")) {
        extension = ".pdf";
      }

      // Create blob with correct content type
      const blob = new Blob([response.data], { type: contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Set filename with correct extension
      let filename = `${student?.studentCode}${extension}`;

      // Try to get filename from headers
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const matches = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      // Ensure correct extension
      if (!filename.toLowerCase().endsWith(extension)) {
        const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
        filename = `${nameWithoutExt}${extension}`;
      }

      link.setAttribute("download", filename);

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      setError(true);
      setErrorMessage("Failed to download file");
    }
  };

  // Additional Info CollapsibleCard Content
  const renderAdditionalInfoContent = () => {
    if (isEditing) {
      // Edit mode for additional info
      return (
        <div className="o-column o-column--gap-size_700">
          {/* STUDENT SECTION */}
          <section className="o-column o-column--gap-size_500">
            <div>
              <SectionDivider title={"studentInfo"} />
              {/* Birthdate */}
              <div className="o-column o-column--gap-size_400">
                <label className="t-bold_bold">{t("birth")}: </label>
                <div className="o-row o-row--gap-size_500">
                  <div>
                    <label>{t("day")}:</label>
                    <CustomInput
                      type="number"
                      value={additionalFields.dayOfBirth}
                      onChange={(e) =>
                        handleAdditionalInputChange(e, "dayOfBirth")
                      }
                    />
                  </div>
                  <div>
                    <label>{t("month")}:</label>
                    <CustomInput
                      type="number"
                      value={additionalFields.monthOfBirth}
                      onChange={(e) =>
                        handleAdditionalInputChange(e, "monthOfBirth")
                      }
                    />
                  </div>
                  <div>
                    <label>{t("year")}:</label>
                    <CustomInput
                      type="number"
                      value={additionalFields.yearOfBirth}
                      onChange={(e) =>
                        handleAdditionalInputChange(e, "yearOfBirth")
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ID */}
            <div>
              <label>{t("unifiedIdNumber")}:</label>
              <CustomInput
                type="text"
                value={additionalFields.unifiedCardNumber}
                onChange={(e) =>
                  handleAdditionalInputChange(e, "unifiedCardNumber")
                }
              />

              <p className="t-bold_bold">{t("noUnifiedIdNumber")}</p>
              <label>{t("civilStatusIdNumber")}:</label>
              <CustomInput
                type="text"
                value={additionalFields.civilStatusIdNumber}
                onChange={(e) =>
                  handleAdditionalInputChange(e, "civilStatusIdNumber")
                }
              />
            </div>

            {/* ADDRESS */}
            <div className="o-column o-column--gap-size_400">
              <label className="t-bold_bold">{t("currentAddress")}:</label>
              <div className="o-row o-row--gap-size_500">
                <div>
                  <label>{t("mahala")}:</label>
                  <CustomInput
                    type="text"
                    value={additionalFields.mahala}
                    onChange={(e) => handleAdditionalInputChange(e, "mahala")}
                  />
                </div>
                <div>
                  <label>{t("zoqaq")}:</label>
                  <CustomInput
                    type="text"
                    value={additionalFields.zoqaq}
                    onChange={(e) => handleAdditionalInputChange(e, "zoqaq")}
                  />
                </div>
                <div>
                  <label>{t("dar")}:</label>
                  <CustomInput
                    type="text"
                    value={additionalFields.dar}
                    onChange={(e) => handleAdditionalInputChange(e, "dar")}
                  />
                </div>
              </div>

              <div>
                <label>{t("nearestLandMark")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.nearestPoint}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "nearestPoint")
                  }
                />
              </div>
            </div>

            {/* FAMILY  */}
            <div className="o-column o-column--gap-size_400">
              <label className="t-bold_bold">{t("family")}:</label>
              <div className="o-row o-row--gap-size_400">
                <div>
                  <label>{t("numberOfFamilyMembers")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.numberOfFamilyNumber}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "numberOfFamilyNumber")
                    }
                  />
                </div>
                <div>
                  <label>{t("positionAmoungSiblings")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.studentOrderAmongFamily}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "studentOrderAmongFamily")
                    }
                  />
                </div>
                <div>
                  <label>{t("livesWith")}</label>
                  <CustomInput
                    type="text"
                    value={additionalFields.livingWith}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "livingWith")
                    }
                  />
                </div>
              </div>
            </div>
          </section>

          {/* FATHER SECTION */}
          <section className="o-column o-column--gap-size_500">
            <div>
              <SectionDivider title={"fatherInfo"} />
              <div>
                <label>{t("fatherFullName")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherName}
                  onChange={(e) => handleAdditionalInputChange(e, "fatherName")}
                />
              </div>
            </div>

            <div className="o-row o-row--gap-size_200 o-row--vertical-align_center">
              <label>{t("fatherIsAlive")}:</label>
              <Dropdown
                options={[
                  { title: t("yes"), value: true },
                  { title: t("no"), value: false },
                ]}
                selected={{
                  title:
                    additionalFields.fatherAlive === true
                      ? t("yes")
                      : additionalFields.fatherAlive === false
                      ? t("no")
                      : "",
                  value: additionalFields.fatherAlive,
                }}
                setSelected={(selected) => {
                  handleAdditionalInputChange(
                    {
                      target: {
                        value: selected.value,
                      },
                    },
                    "fatherAlive"
                  );
                }}
              />
            </div>

            {/* Father ID */}
            <div className="o-column o-column--gap-size_400">
              <div>
                <label>{t("unifiedIdNumber")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherUnifiedCardNumber}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "fatherUnifiedCardNumber")
                  }
                />
              </div>

              <p className="t-bold_bold">{t("noUnifiedIdNumber")}</p>

              <div>
                <label>{t("civilStatusIdNumber")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherCivilStatusIdNumber}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "fatherCivilStatusIdNumber")
                  }
                />
              </div>

              <div>
                <label>{t("idCertificateNumber")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherNationalityCertificateNumber}
                  onChange={(e) =>
                    handleAdditionalInputChange(
                      e,
                      "fatherNationalityCertificateNumber"
                    )
                  }
                />
              </div>
            </div>

            {/* Father Birthdate */}
            <div className="o-column o-column--gap-size_400">
              <label className="t-bold_bold">{t("birth")}: </label>
              <div className="o-row o-row--gap-size_500">
                <div>
                  <label>{t("day")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.fatherDayOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "fatherDayOfBirth")
                    }
                  />
                </div>
                <div>
                  <label>{t("month")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.fatherMonthOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "fatherMonthOfBirth")
                    }
                  />
                </div>
                <div>
                  <label>{t("year")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.fatherYearOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "fatherYearOfBirth")
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <label>{t("phoneNumber")}:</label>
              <CustomInput
                type="text"
                value={additionalFields.fatherPhoneNumber}
                onChange={(e) =>
                  handleAdditionalInputChange(e, "fatherPhoneNumber")
                }
              />
            </div>

            <div className="o-row o-row--gap-size_400">
              <div>
                <label>{t("education")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherAcademicAchievement}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "fatherAcademicAchievement")
                  }
                />
              </div>
              <div>
                <label>{t("currentJob")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.fatherCurrentJob}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "fatherCurrentJob")
                  }
                />
              </div>
            </div>
          </section>

          {/* MOTHER SECTION */}
          <section className="o-column o-column--gap-size_500">
            <div>
              <SectionDivider title={"motherInfo"} />
              <div>
                <label>{t("motherFullName")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.motherName}
                  onChange={(e) => handleAdditionalInputChange(e, "motherName")}
                />
              </div>
            </div>

            <div className="o-row o-row--gap-size_200 o-row--vertical-align_center">
              <label>{t("motherIsAlive")}:</label>
              <Dropdown
                options={[
                  { title: t("yes"), value: true },
                  { title: t("no"), value: false },
                ]}
                selected={{
                  title:
                    additionalFields.motherAlive === true
                      ? t("yes")
                      : additionalFields.motherAlive === false
                      ? t("no")
                      : "",
                  value: additionalFields.motherAlive,
                }}
                setSelected={(selected) => {
                  handleAdditionalInputChange(
                    {
                      target: {
                        value: selected.value,
                      },
                    },
                    "motherAlive"
                  );
                }}
              />
            </div>

            {/* Mother Birthdate */}
            <div className="o-column o-column--gap-size_400">
              <label className="t-bold_bold">{t("birth")}: </label>
              <div className="o-row o-row--gap-size_500">
                <div>
                  <label>{t("day")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.motherDayOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "motherDayOfBirth")
                    }
                  />
                </div>
                <div>
                  <label>{t("month")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.motherMonthOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "motherMonthOfBirth")
                    }
                  />
                </div>
                <div>
                  <label>{t("year")}:</label>
                  <CustomInput
                    type="number"
                    value={additionalFields.motherYearOfBirth}
                    onChange={(e) =>
                      handleAdditionalInputChange(e, "motherYearOfBirth")
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <label>{t("phoneNumber")}:</label>
              <CustomInput
                type="text"
                value={additionalFields.motherPhoneNumber}
                onChange={(e) =>
                  handleAdditionalInputChange(e, "motherPhoneNumber")
                }
              />
            </div>

            <div className="o-row o-row--gap-size_400">
              <div>
                <label>{t("education")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.motherAcademicAchievement}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "motherAcademicAchievement")
                  }
                />
              </div>
              <div>
                <label>{t("currentJob")}:</label>
                <CustomInput
                  type="text"
                  value={additionalFields.motherCurrentJob}
                  onChange={(e) =>
                    handleAdditionalInputChange(e, "motherCurrentJob")
                  }
                />
              </div>
            </div>
          </section>

          {/* Additional Info Action Buttons */}
          {/* <div
            className="o-row o-row--gap-size_400"
            style={{ justifyContent: "flex-end", marginTop: "20px" }}
          >
            <CustomButton
              title={t("confirm")}
              onClick={handleUpdateAdditionalInfo}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={() => setIsEditingAdditional(false)}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </div> */}
        </div>
      );
    } else {
      // View mode for additional info
      return (
        <div className="o-column o-column--gap-size_700">
          {additionalInfo ? (
            <>
              {/* STUDENT SECTION */}
              <section className="o-column o-column--gap-size_500">
                <SectionDivider title={"studentInfo"} />

                {/* Birthdate */}
                {(additionalInfo.dayOfBirth ||
                  additionalInfo.monthOfBirth ||
                  additionalInfo.yearOfBirth) && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("birth")}:</p>
                    <p variant="body1">
                      {additionalInfo.dayOfBirth || "N/A"}/
                      {additionalInfo.monthOfBirth || "N/A"}/
                      {additionalInfo.yearOfBirth || "N/A"}
                    </p>
                  </div>
                )}

                {/* ID Numbers */}
                {additionalInfo.unifiedCardNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("unifiedIdNumber")}:</p>
                    <p variant="body1">{additionalInfo.unifiedCardNumber}</p>
                  </div>
                )}

                {additionalInfo.civilStatusIdNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("civilStatusIdNumber")}:</p>
                    <p variant="body1">{additionalInfo.civilStatusIdNumber}</p>
                  </div>
                )}

                {/* Address */}
                {(additionalInfo.mahala ||
                  additionalInfo.zoqaq ||
                  additionalInfo.dar) && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("currentAddress")}:</p>
                    <p variant="body1">
                      {t("mahala")}: {additionalInfo.mahala || "N/A"},{" "}
                      {t("zoqaq")}: {additionalInfo.zoqaq || "N/A"}, {t("dar")}:{" "}
                      {additionalInfo.dar || "N/A"}
                    </p>
                  </div>
                )}

                {additionalInfo.nearestPoint && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("nearestLandMark")}:</p>
                    <p variant="body1">{additionalInfo.nearestPoint}</p>
                  </div>
                )}

                {/* Family Info */}
                {additionalInfo.numberOfFamilyNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("numberOfFamilyMembers")}:</p>
                    <p variant="body1">{additionalInfo.numberOfFamilyNumber}</p>
                  </div>
                )}

                {additionalInfo.studentOrderAmongFamily && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("positionAmoungSiblings")}:</p>
                    <p variant="body1">
                      {additionalInfo.studentOrderAmongFamily}
                    </p>
                  </div>
                )}

                {additionalInfo.livingWith && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("livesWith")}:</p>
                    <p variant="body1">{additionalInfo.livingWith}</p>
                  </div>
                )}
              </section>

              {/* FATHER SECTION */}
              <section className="o-column o-column--gap-size_500">
                <SectionDivider title={"fatherInfo"} />

                {additionalInfo.fatherName && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("fatherFullName")}:</p>
                    <p variant="body1">{additionalInfo.fatherName}</p>
                  </div>
                )}

                {additionalInfo.fatherAlive !== null && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("fatherIsAlive")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherAlive ? t("yes") : t("no")}
                    </p>
                  </div>
                )}

                {additionalInfo.fatherUnifiedCardNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("unifiedIdNumber")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherUnifiedCardNumber}
                    </p>
                  </div>
                )}

                {additionalInfo.fatherCivilStatusIdNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("civilStatusIdNumber")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherCivilStatusIdNumber}
                    </p>
                  </div>
                )}

                {additionalInfo.fatherNationalityCertificateNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("idCertificateNumber")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherNationalityCertificateNumber}
                    </p>
                  </div>
                )}

                {(additionalInfo.fatherDayOfBirth ||
                  additionalInfo.fatherMonthOfBirth ||
                  additionalInfo.fatherYearOfBirth) && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("birth")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherDayOfBirth || "N/A"}/
                      {additionalInfo.fatherMonthOfBirth || "N/A"}/
                      {additionalInfo.fatherYearOfBirth || "N/A"}
                    </p>
                  </div>
                )}

                {additionalInfo.fatherPhoneNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("phoneNumber")}:</p>
                    <p variant="body1">{additionalInfo.fatherPhoneNumber}</p>
                  </div>
                )}

                {additionalInfo.fatherAcademicAchievement && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("education")}:</p>
                    <p variant="body1">
                      {additionalInfo.fatherAcademicAchievement}
                    </p>
                  </div>
                )}

                {additionalInfo.fatherCurrentJob && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("currentJob")}:</p>
                    <p variant="body1">{additionalInfo.fatherCurrentJob}</p>
                  </div>
                )}
              </section>

              {/* MOTHER SECTION */}
              <section className="o-column o-column--gap-size_500">
                <SectionDivider title={"motherInfo"} />

                {additionalInfo.motherName && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("motherFullName")}:</p>
                    <p variant="body1">{additionalInfo.motherName}</p>
                  </div>
                )}

                {additionalInfo.motherAlive !== null && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("motherIsAlive")}:</p>
                    <p variant="body1">
                      {additionalInfo.motherAlive ? t("yes") : t("no")}
                    </p>
                  </div>
                )}

                {(additionalInfo.motherDayOfBirth ||
                  additionalInfo.motherMonthOfBirth ||
                  additionalInfo.motherYearOfBirth) && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("birth")}:</p>
                    <p variant="body1">
                      {additionalInfo.motherDayOfBirth || "N/A"}/
                      {additionalInfo.motherMonthOfBirth || "N/A"}/
                      {additionalInfo.motherYearOfBirth || "N/A"}
                    </p>
                  </div>
                )}

                {additionalInfo.motherPhoneNumber && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("phoneNumber")}:</p>
                    <p variant="body1">{additionalInfo.motherPhoneNumber}</p>
                  </div>
                )}

                {additionalInfo.motherAcademicAchievement && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("education")}:</p>
                    <p variant="body1">
                      {additionalInfo.motherAcademicAchievement}
                    </p>
                  </div>
                )}

                {additionalInfo.motherCurrentJob && (
                  <div className="view-user-entity">
                    <p variant="body1">{t("currentJob")}:</p>
                    <p variant="body1">{additionalInfo.motherCurrentJob}</p>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="view-user-entity">
              <p variant="body1">
                {t("noAdditionalInformation") ||
                  "No additional information available"}
              </p>
            </div>
          )}

          {/* Edit Button for Additional Info */}
          {/* <div
            className="o-row o-row--gap-size_400"
            style={{ justifyContent: "flex-end", marginTop: "20px" }}
          >
            <CustomButton
              variant="blank"
              title={t("edit")}
              onClick={() => setIsEditingAdditional(true)}
              icon={editIcon}
              iconSize={18}
            />
          </div> */}
        </div>
      );
    }
  };

  if (!isOpen || !student) return null;

  // Get active student year for display
  const activeStudentYear = student.activeYear;

  return (
    <>
      <Modal
        title={isEditing ? t("editStudent") : t("student")}
        onClose={handleCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="edit-user-actions">
              {isEditing ? (
                <>
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleUpdateStudent}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancel}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                  <CustomButton
                    variant="red"
                    title={t("delete")}
                    onClick={() => setShowDeleteConfirmation(true)}
                    icon={deleteIcon}
                    iconSize={16}
                  />
                </>
              ) : (
                <>
                  <CustomButton
                    variant="blank"
                    title={t("edit")}
                    onClick={() => setIsEditing(true)}
                    icon={editIcon}
                    iconSize={18}
                  />
                  <CustomButton
                    title={t("downloadStudentDocument")}
                    onClick={handleDownloadStudentInfo}
                    icon={downloadIcon}
                    iconSize={18}
                    variant="blank"
                  />
                </>
              )}
            </section>
          </>
        }
      >
        {isEditing ? (
          <form onSubmit={handleUpdateStudent}>
            <div>
              {error && (
                <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
                  <ErrorBox>{errorMessage}</ErrorBox>
                </div>
              )}
              <div className="edit-user-entity">
                <span>{t("studentName")}: </span>
                <CustomInput
                  name="fullName"
                  value={studentFields.fullName}
                  onChange={(e) => handleInputChange(e, "fullName")}
                  errorText={errorMessage ? t("inputFieldCannotBeEmpty") : ""}
                />

                <span>{t("studentId")}: </span>
                <CustomInput
                  name="studentId"
                  value={student.studentCode}
                  disabled={true}
                  readOnly={true}
                />

                <span>{t("academicLevel")}: </span>
                <HotdropdownV2
                  initOptions={[...educationLevels]}
                  setOptions={setEducationLevels}
                  selected={studentFields.academicLevel.title}
                  setSelected={(selected) => {
                    handleInputChange(
                      { target: { value: selected } },
                      "academicLevel"
                    );

                    setStudentFields((prev) => ({
                      ...prev,
                      subclass: {
                        id: "",
                        name: "",
                      },
                    }));

                    if (selected.value) {
                      fetchSubclasses(selected.value);
                    } else {
                      setSubclasses([]);
                    }
                  }}
                  getData={(page, limit, isDropDown, query) =>
                    fetchEducationLevels(page, limit, isDropDown, query)
                  }
                  optionsName="educationLevels"
                  thick
                />

                {studentFields.academicLevel.title && (
                  <>
                    <span>{t("subclass")}: </span>
                    <Dropdown
                      options={subclasses}
                      selected={{
                        title: studentFields.subclass.name
                          ? subclasses.find(
                              (sub) => sub.value === studentFields.subclass.id
                            )?.title || ""
                          : "",
                        value: studentFields.subclass.id,
                      }}
                      setSelected={(selected) => {
                        setStudentFields((prev) => ({
                          ...prev,
                          subclass: {
                            id: selected.value,
                            name: selected.name,
                          },
                        }));
                      }}
                      thick
                    />
                  </>
                )}

                <span>{t("source")}: </span>
                <Dropdown
                  options={[
                    { title: "kg2", value: "kg2" },
                    { title: "continues", value: "continues" },
                    { title: "newStudent", value: "newStudent" },
                    { title: "transition", value: "transition" },
                  ]}
                  selected={{
                    title: studentFields.source,
                    value: studentFields.source,
                  }}
                  setSelected={(selected) => {
                    handleInputChange(
                      {
                        target: {
                          value: selected.value,
                        },
                      },
                      "source"
                    );
                  }}
                  thick
                />

                <span>{t("discount")}: </span>
                <HotdropdownV2
                  initOptions={[...discounts]}
                  setOptions={setDiscounts}
                  selected={t(studentFields.discount?.title)}
                  setSelected={(selected) => {
                    handleInputChange(
                      { target: { value: selected } },
                      "discount"
                    );
                  }}
                  getData={fetchAllDiscounts}
                  optionsName="discounts"
                  thick
                />

                {studentFields.discount.title == "other" &&
                studentFields.discount.value == "" ? (
                  <DiscountSection
                    originalPrice={studentFields.academicLevel.price}
                    onPriceChange={() => {}}
                    inputMaxWidth="u-max-width_500"
                    setParentDiscountType={setDiscountType}
                    setParentDiscountAmount={(value) =>
                      handleInputChange({ target: { value } }, "discountAmount")
                    }
                    initialDiscountAmount={studentFields.discountAmount}
                    initialDiscountType={discountType}
                  />
                ) : null}

                <span>{t("phoneNumber")}: </span>
                <CustomInput
                  name="phoneNumber"
                  value={studentFields.phoneNumber}
                  onChange={(e) => handleInputChange(e, "phoneNumber")}
                />

                <span>
                  {t("secondPhoneNumber")} ({t("optional")}):{" "}
                </span>
                <CustomInput
                  name="secondPhoneNumber"
                  value={studentFields.secondPhoneNumber}
                  onChange={(e) => handleInputChange(e, "secondPhoneNumber")}
                />

                <span>{t("notes")}:</span>
                <CustomInput
                  name="notes"
                  value={studentFields.notes}
                  onChange={(e) => {
                    handleInputChange(e, "notes");
                  }}
                  id="notesInput"
                  type="textarea"
                  flex
                />

                <div className="u-padding--bottom_700 u-padding--top_400">
                  <CollapsibleCard
                    title={t("extraStudentInfo")}
                    body={renderAdditionalInfoContent()}
                  />
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div>
            <div className="view-user-entity">
              <p variant="body1">{t("studentName")}:</p>
              <p variant="body1">{student.fullName}</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("studentId")}:</p>
              <p variant="body1">{student.studentCode}</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("academicLevel")}:</p>
              <p variant="body1">{t(activeStudentYear?.schoolClass?.name)}</p>
            </div>

            {activeStudentYear?.schoolSubClass && (
              <div className="view-user-entity">
                <p variant="body1">{t("subclass")}:</p>
                <p variant="body1">{activeStudentYear.schoolSubClass.name}</p>
              </div>
            )}

            <div className="view-user-entity">
              <p variant="body1">{t("source")}:</p>
              <p variant="body1">{t(activeStudentYear?.source) || "N/A"}</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("discount")}:</p>
              <p variant="body1">
                {activeStudentYear?.discount !== null
                  ? t(activeStudentYear?.discount?.name)
                  : activeStudentYear.discountAmount
                  ? t("other")
                  : t("noDiscount")}
              </p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("phoneNumber")}:</p>
              <p variant="body1">{student.phoneNumber1}</p>
            </div>

            {student.phoneNumber2 && (
              <div className="view-user-entity">
                <p variant="body1">{t("secondPhoneNumber")}:</p>
                <p variant="body1">{student.phoneNumber2}</p>
              </div>
            )}

            {student.notes && (
              <div className="view-user-entity">
                <p variant="body1">{t("notes")}:</p>
                <p variant="body1">{student.notes}</p>
              </div>
            )}

            {/* Additional Information CollapsibleCard */}
            <div className="u-padding--bottom_700 u-padding--top_400">
              <CollapsibleCard
                title={t("extraStudentInfo")}
                body={renderAdditionalInfoContent()}
              />
            </div>
          </div>
        )}
      </Modal>

      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteStudent}
          customMessage={t("deleteStudent")}
          closeTitle="cancel"
          confirmTitle="delete"
          loading={loadingModal}
          cancel

        />
      )}
    </>
  );
};

export default EditStudentModal;
